/**
 * PDF attachment panel. Stories: US-A05 (part of W-021).
 *
 * AC1 stored and linked to the lesson
 * AC2 a non-PDF is refused
 * AC3 a file over the limit shows the limit and does not start
 * AC4 replacing removes the previous file
 *
 * PDFs live in a private Bunny Storage zone and are served through a signed-URL
 * edge function (docs/03 §5.3). They are small enough that a plain POST to that
 * function beats a resumable transfer.
 */
import { DeleteOutlined, FilePdfOutlined } from '@ant-design/icons'
import { Alert, Button, Space, Typography, Upload } from 'antd'
import type { UploadProps } from 'antd'
import { useState } from 'react'
import { useAdminTranslate } from '@/providers/i18n-provider'
import { invokeEdgeFunction } from '@/lib/supabase'
import { formatBytes } from '@/lib/upload-manager'

/**
 * TODO(US-A05): the ceiling is a server-side configuration value; the docs do
 * not state it. 25 MB is the working limit for this scaffold — read it from the
 * edge function's response once `media-upload-pdf` exists so the two cannot
 * disagree.
 */
export const MAX_PDF_BYTES = 25 * 1024 * 1024

export interface PdfUploadPanelProps {
  contentId: string
  pdfPath: string | null
  disabled?: boolean
  onChange: (pdfPath: string | null) => void
}

export const PdfUploadPanel = ({
  contentId,
  pdfPath,
  disabled = false,
  onChange,
}: PdfUploadPanelProps) => {
  const translate = useAdminTranslate()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const beforeUpload: UploadProps['beforeUpload'] = file => {
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError(translate('admin.screens.pdfPanel.onlyPdf')) // AC2
      return Upload.LIST_IGNORE
    }
    if (file.size > MAX_PDF_BYTES) {
      // AC3
      setError(
        translate('admin.screens.pdfPanel.tooLarge', {
          size: formatBytes(file.size),
          limit: formatBytes(MAX_PDF_BYTES),
        }),
      )
      return Upload.LIST_IGNORE
    }

    setError(null)
    setBusy(true)
    void (async () => {
      try {
        const form = new FormData()
        form.append('file', file)
        form.append('content_id', contentId)
        // TODO(US-A05): edge-function name owned by the backend agent.
        // `media-upload-pdf` is the working name; it must delete the previous
        // object when replacing one (AC4) so the storage zone does not grow
        // with orphans.
        const result = await invokeEdgeFunction<{ pdf_path: string }>('media-upload-pdf', form)
        onChange(result.pdf_path)
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : translate('admin.screens.pdfPanel.uploadFailed'),
        )
      } finally {
        setBusy(false)
      }
    })()

    return false
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text strong>{translate('admin.screens.pdfPanel.title')}</Typography.Text>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      {pdfPath ? (
        <Space>
          <FilePdfOutlined />
          <Typography.Text code>{pdfPath}</Typography.Text>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            {translate('admin.screens.pdfPanel.remove')}
          </Button>
        </Space>
      ) : null}

      <Upload
        showUploadList={false}
        accept="application/pdf,.pdf"
        beforeUpload={beforeUpload}
        disabled={disabled || busy}
      >
        <Button loading={busy} disabled={disabled}>
          {pdfPath
            ? translate('admin.screens.pdfPanel.replace')
            : translate('admin.screens.pdfPanel.attach')}
        </Button>
      </Upload>

      <Typography.Text type="secondary">
        {translate('admin.screens.pdfPanel.maxSize', { limit: formatBytes(MAX_PDF_BYTES) })}
      </Typography.Text>
    </Space>
  )
}
